import { useEffect, useId, useRef, type ReactNode } from "react";
import { focusIsLost } from "../a11y";
import type { Settings, Theme } from "../settings";

/**
 * Native <dialog> gives a modal focus trap and Esc for free. Focus goes back to whatever opened the dialog when it
 * closes, including when the dialog is simply unmounted (which the browser does not do by itself).
 */
export function Modal({ title, open, onClose, children }: { title: string; open: boolean; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      d.showModal();
      // React's autoFocus runs before the dialog is shown, so the browser would focus the close button instead.
      d.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    }
    if (!open) {
      if (d.open) d.close();
      if (opener.current) { restore(opener.current); opener.current = null; }
    }
  }, [open]);
  useEffect(() => () => { if (opener.current) restore(opener.current); }, []);
  return (
    <dialog ref={ref} className="modal" aria-labelledby={titleId} onClose={onClose}>
      <div className="modal__head">
        <h2 id={titleId}>{title}</h2>
        <button type="button" className="btn btn--icon" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="modal__body">{children}</div>
    </dialog>
  );
}

/** Only if focus has been lost: the page may already have moved it somewhere better (a new panel's heading). */
function restore(el: HTMLElement | null) {
  requestAnimationFrame(() => {
    if (!focusIsLost() || document.querySelector("dialog[open]")) return;
    if (el?.isConnected) el.focus({ preventScroll: true });
    else document.querySelector<HTMLElement>("#stage")?.focus({ preventScroll: true });
  });
}

export function ControlsHelp({ shortcuts = true }: { shortcuts?: boolean }) {
  const rows: [string, string][] = [
    ["1 – 9", "Choose a response"],
    ["Enter", "See the person at the door, or continue"],
    ["Esc", "Back to the office"],
    ["O", "Office"],
    ["P", "People"],
    ["W", "Who's who"],
    ["H", "Hats"],
    ["S", "Standing instructions"],
    ["R", "Processes"],
    ["D", "Diary"],
    ["I", "Inbox"],
    ["A", "Accounts"],
    ["U", "Suppliers"],
    ["?", "This help"],
  ];
  return (
    <>
      <p>People come to your door. Click someone in the queue to see who they are and what it is about. Hover over, focus or tap a name with a dotted underline to see who they are; <kbd>Esc</kbd> closes it.</p>
      {!shortcuts && <p><strong>Single-key shortcuts are off.</strong> Only Esc works until you turn them on in the menu.</p>}
      <table className="keys">
        <caption className="visually-hidden">Keyboard shortcuts</caption>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}><th scope="row"><kbd>{k}</kbd></th><td>{v}</td></tr>
          ))}
        </tbody>
      </table>
      <p className="muted">Shortcuts are single keys and are ignored while you are typing. You can turn them off in the menu.</p>
    </>
  );
}

export function SettingsForm({ settings, onChange }: { settings: Settings; onChange: (s: Partial<Settings>) => void }) {
  const themes: [Theme, string][] = [["system", "Match my device"], ["light", "Light"], ["dark", "Dark"]];
  return (
    <form className="settings" onSubmit={(e) => e.preventDefault()}>
      <fieldset>
        <legend>Theme</legend>
        {themes.map(([t, label]) => (
          <label key={t} className="check">
            <input type="radio" name="theme" checked={settings.theme === t} onChange={() => onChange({ theme: t })} />
            <span>{label}</span>
          </label>
        ))}
      </fieldset>
      <label className="check">
        <input type="checkbox" checked={settings.reducedMotion} onChange={(e) => onChange({ reducedMotion: e.target.checked })} />
        <span>Reduce motion</span>
      </label>
      <label className="check">
        <input type="checkbox" checked={settings.shortcuts} onChange={(e) => onChange({ shortcuts: e.target.checked })}
          aria-describedby="shortcuts-note" />
        <span>Single-key shortcuts</span>
      </label>
      <p id="shortcuts-note" className="muted small">Letters, numbers, Enter and ? act straight away. Turn this off if you use speech input or press keys by accident.</p>
    </form>
  );
}
